import type { FileDiffOptions } from '@pierre/diffs';
import type { CSSProperties } from 'react';

const PIERRE_DIFF_COLOR_TOKENS = {
  // Neutral dark gray base rather than pure black: pure black pushes light text past a
  // comfortable contrast (halation/eye strain) and gives the dim add/remove tints no neutral
  // reference, so their hue is hard to perceive at near-zero luminance.
  '--diffs-bg': '#18181b',
  '--diffs-fg': '#e4e4e7',
  '--diffs-bg-buffer-override': '#18181b',
  '--diffs-bg-context-override': '#18181b',
  '--diffs-bg-separator-override': '#27272a',
  '--diffs-bg-hover-override': '#202023',
  // Lifted to keep line numbers above WCAG AA (4.5:1) against the lighter neutral base.
  '--diffs-fg-number-override': '#868d91',
  '--diffs-fg-number-addition-override': '#a0aaab',
  '--diffs-fg-number-deletion-override': '#a0aaab',
  '--diffs-addition-color-override': '#e4e4e7',
  '--diffs-deletion-color-override': '#e4e4e7',
  '--diffs-modified-color-override': '#e4e4e7',
  // Add/remove ramp: row < number/hover < emphasis. The `-emphasis` (intraline word/char)
  // tints are intentionally much brighter/more saturated than the row so the actual changed
  // tokens pop, mirroring how GitHub/GitLab layer a strong word highlight over a dim row.
  '--diffs-bg-addition-override': '#213328',
  '--diffs-bg-addition-number-override': '#1d3d27',
  '--diffs-bg-addition-hover-override': '#204329',
  '--diffs-bg-addition-emphasis-override': '#264130',
  '--diffs-bg-deletion-override': '#391822',
  '--diffs-bg-deletion-number-override': '#43222a',
  '--diffs-bg-deletion-hover-override': '#49252d',
  '--diffs-bg-deletion-emphasis-override': '#51222E',
  '--diffs-selection-color-override': '#e4e4e7',
  '--diffs-bg-selection-override': '#2d4a5a',
  '--diffs-bg-selection-number-override': '#2d4a5a',
  '--diffs-bg-selection-background-override': '#2d4a5a',
  '--diffs-bg-selection-number-background-override': '#2d4a5a',
  '--diffs-gap-inline': '0px',
  '--diffs-gap-block': '0px',
} as const satisfies CSSProperties;

export const PIERRE_DIFF_PALETTE_OVERRIDES = {
  ...PIERRE_DIFF_COLOR_TOKENS,
  '--diffs-font-size': '12px',
  '--diffs-line-height': '1.47',
} as const satisfies CSSProperties;

export const PIERRE_COMPACT_DIFF_PALETTE_OVERRIDES = {
  ...PIERRE_DIFF_COLOR_TOKENS,
  '--diffs-font-size': '11px',
  '--diffs-line-height': '18px',
} as const satisfies CSSProperties;

export const PIERRE_DIFF_ROW_BACKGROUND_UNSAFE_CSS = `
/* @pierre/diffs 1.1.21 mixes row overrides with the base background. PR v2 keeps these
   direct so the restored palette matches the pre-1.1.21 visual colors. */
:where([data-background]) [data-line-type='change-addition']:where([data-line], [data-no-newline]) {
  --diffs-line-bg: var(--diffs-bg-addition-override, var(--diffs-bg-addition));
  background-color: var(--diffs-bg-addition-override, var(--diffs-bg-addition)) !important;
}

:where([data-background]) [data-line-type='change-deletion']:where([data-line], [data-no-newline]) {
  --diffs-line-bg: var(--diffs-bg-deletion-override, var(--diffs-bg-deletion));
  background-color: var(--diffs-bg-deletion-override, var(--diffs-bg-deletion)) !important;
}

:where([data-background]) [data-line-type='change-addition']:where([data-gutter-buffer], [data-column-number]) {
  --diffs-line-bg: var(
    --diffs-bg-addition-number-override,
    var(--diffs-bg-addition-number)
  );
  background-color: var(
    --diffs-bg-addition-number-override,
    var(--diffs-bg-addition-number)
  ) !important;
}

:where([data-background]) [data-line-type='change-deletion']:where([data-gutter-buffer], [data-column-number]) {
  --diffs-line-bg: var(
    --diffs-bg-deletion-number-override,
    var(--diffs-bg-deletion-number)
  );
  background-color: var(
    --diffs-bg-deletion-number-override,
    var(--diffs-bg-deletion-number)
  ) !important;
}

@media (pointer: fine) {
  :where([data-background]) [data-line-type='change-addition'][data-hovered]:where([data-line], [data-no-newline]) {
    --diffs-line-bg: var(
      --diffs-bg-addition-hover-override,
      var(--diffs-bg-addition-override, var(--diffs-bg-addition))
    );
    background-color: var(
      --diffs-bg-addition-hover-override,
      var(--diffs-bg-addition-override, var(--diffs-bg-addition))
    ) !important;
  }

  :where([data-background]) [data-line-type='change-deletion'][data-hovered]:where([data-line], [data-no-newline]) {
    --diffs-line-bg: var(
      --diffs-bg-deletion-hover-override,
      var(--diffs-bg-deletion-override, var(--diffs-bg-deletion))
    );
    background-color: var(
      --diffs-bg-deletion-hover-override,
      var(--diffs-bg-deletion-override, var(--diffs-bg-deletion))
    ) !important;
  }
}

/* Keep EOF newline markers visually aligned with surrounding added/removed rows.
   Pierre renders these as special metadata rows, so we restate the row colors
   explicitly to avoid a mismatched band at the end of full-file diffs. */
[data-no-newline][data-line-type='change-addition'] {
  --diffs-line-bg: var(--diffs-bg-addition-override, var(--diffs-bg-addition));
  background-color: var(--diffs-bg-addition-override, var(--diffs-bg-addition)) !important;
}

[data-no-newline][data-line-type='change-deletion'] {
  --diffs-line-bg: var(--diffs-bg-deletion-override, var(--diffs-bg-deletion));
  background-color: var(--diffs-bg-deletion-override, var(--diffs-bg-deletion)) !important;
}

[data-no-newline][data-line-type='change-addition'] span,
[data-no-newline][data-line-type='change-deletion'] span {
  background-color: transparent !important;
}

[data-gutter-buffer='metadata'][data-line-type='change-addition'] {
  --diffs-line-bg: var(
    --diffs-bg-addition-number-override,
    var(--diffs-bg-addition-number)
  );
  background-color: var(
    --diffs-bg-addition-number-override,
    var(--diffs-bg-addition-number)
  ) !important;
}

[data-gutter-buffer='metadata'][data-line-type='change-deletion'] {
  --diffs-line-bg: var(
    --diffs-bg-deletion-number-override,
    var(--diffs-bg-deletion-number)
  );
  background-color: var(
    --diffs-bg-deletion-number-override,
    var(--diffs-bg-deletion-number)
  ) !important;
}
`;

/**
 * A unified, word-level, wrapped diff with the row colors above and no file
 * header. `[data-code]` hides the horizontal scrollbar wrapping leaves behind,
 * and the room Pierre keeps for a scrollbar beside it: with scrollbars always
 * shown, that room is an empty band at the right of every row.
 */
export const PIERRE_UNIFIED_DIFF_OPTIONS = {
  diffStyle: 'unified',
  disableFileHeader: true,
  theme: {
    dark: 'pierre-dark',
    light: 'pierre-light',
  },
  themeType: 'dark',
  diffIndicators: 'none',
  lineDiffType: 'word-alt',
  overflow: 'wrap',
  expandUnchanged: false,
  hunkSeparators: 'simple',
  unsafeCSS: `${PIERRE_DIFF_ROW_BACKGROUND_UNSAFE_CSS}\n[data-code] { overflow-x: hidden; scrollbar-gutter: auto; }`,
} as const satisfies FileDiffOptions<undefined, undefined>;
